import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../config.js";
import Pagination from "../Pagination/Paginations.js";
import EntriesSelector from "../Pagination/EntriesSelector";
import DownloadButtons from "../DownloadButtons/DownloadButtons.js";
import "../preloading.css";

const EmployeeReport = () => {
  const today = new Date().toISOString().split("T")[0];

  const [filters, setFilters] = useState({
    start_date: today,
    end_date: today,
    status_type: "-1",
    action_type: "Fetch",
  });

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  // ✅ Handle input changes
  const handleInput = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Fetch employee report
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(
        API_BASE_URL + "/employee_report", // 🔹 Laravel endpoint
        {
          start_date: filters.start_date,
          end_date: filters.end_date,
          status_type: filters.status_type,
          action_type: filters.action_type,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (res.data.status === 200 || Array.isArray(res.data.records)) {
        setRecords(res.data.records || []);
        toast.success("Employee report loaded successfully!", {
          position: "bottom-right",
          theme: "colored",
        });
      } else {
        setRecords([]);
        toast.warn(res.data.message || "No records found", {
          position: "bottom-right",
          theme: "colored",
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Error fetching employee report", {
        position: "bottom-right",
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Filter + Pagination Logic
  const filteredItems = records.filter((item) =>
    `${item.employee_id}${item.employee_name}${item.department}${item.date_of_joining}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredItems.length / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const paginatedData = filteredItems.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <>
      <div className="container-fluid mt-4">
        <ToastContainer />

        {/* 🔹 Filter Form */}
        <div className="card shadow-sm mb-4">
          <div className="card-header with-elements">
            <span className="card-header-title me-2">
              Filter By Join Date
            </span>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row g-3 align-items-end">
                {/* From Date */}
                <div className="col-md-2">
                  <label htmlFor="start_date">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="start_date"
                    value={filters.start_date}
                    onChange={handleInput}
                    required
                  />
                </div>

                {/* To Date */}
                <div className="col-md-2">
                  <label htmlFor="end_date">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="end_date"
                    value={filters.end_date}
                    onChange={handleInput}
                    required
                  />
                </div>

                {/* Status */}
                <div className="col-md-2">
                  <label htmlFor="status_type">Status</label>
                  <select
                    className="form-control"
                    name="status_type"
                    value={filters.status_type}
                    onChange={handleInput}
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                    <option value="-1">All</option>
                  </select>
                </div>

                {/* Action */}
               

                {/* Submit */}
                <div className="col-md-2">
                  <button type="submit" className="btn btn-primary w-100">
                    Get
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* 🔹 Table Section */}
        <div className="card">
          <h6 className="card-header">Employee Report</h6>
          <div className="card-body">
            {/* Top Controls */}
            <div className="d-flex justify-content-between mb-2 align-items-center">
              <div
                className="d-flex align-items-center gap-2"
                style={{ width: "auto", minWidth: "220px" }}
              >
                <DownloadButtons data={filteredItems} filename="Employee_Report" />
                <EntriesSelector
                  setEntriesPerPage={setEntriesPerPage}
                  setCurrentPage={setCurrentPage}
                />
              </div>

              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search..."
                style={{ width: "200px" }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Table */}
            {loading ? (
              <div className="d-flex justify-content-center p-5">
                <div className="spinner"></div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered table-striped">
                  <thead style={{ backgroundColor: "#7d858369" }}>
                    <tr>
                      <th>Employee ID</th>
                      <th>Employee Name</th>
                      <th>Department</th>
                      <th>Date of Joining</th>
                      <th>Status</th>
                      <th>Reason Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((row, idx) => (
                        <tr key={idx}>
                          <td>{row.employee_id}</td>
                          <td>{row.employee_name}</td>
                          <td>{row.department_name}</td>
                          <td>{row.date_of_joining}</td>
                          <td>
                            <span
                              className={`badge ${
                                row.is_active === 1
                                  ? "bg-success"
                                  : "bg-secondary"
                              }`}
                            >
                              {row.is_active === 1 ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>{row.reason_status || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          className="text-center text-danger fw-bold"
                        >
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployeeReport;
